import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: "Newsletter — AiPresidential",
    description: "Field dispatches from the AiPresidential trilogy. Excerpts, behind-the-scenes notes, and launch updates from Victor M. Medina.",
    path: "/newsletter",
  });

  useEffect(() => {
    axios
      .get(`${API}/newsletter-posts`)
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[var(--void)] text-gray-200">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 text-sm font-orbitron tracking-widest uppercase"
          data-testid="newsletter-back-home"
        >
          <ArrowLeft className="w-4 h-4" /> Back Home
        </Link>
        <p className="font-orbitron text-sm tracking-[0.3em] text-cyan-400 uppercase mb-4">Field Dispatch</p>
        <h1 className="font-orbitron text-4xl md:text-6xl font-bold gradient-text mb-10" data-testid="newsletter-title">
          The Awakening Newsletter
        </h1>

        {loading && <p className="text-gray-500" data-testid="newsletter-loading">Loading transmissions…</p>}
        {!loading && posts.length === 0 && (
          <div className="glass rounded-3xl border border-white/10 p-10 text-center" data-testid="newsletter-empty">
            <p className="text-gray-400 mb-2">No posts published yet.</p>
            <p className="text-gray-600 text-sm">Subscribe on the homepage to be first in line.</p>
          </div>
        )}

        <div className="space-y-6">
          {posts.map((p) => (
            <article
              key={p.id}
              className="glass rounded-3xl border border-white/10 overflow-hidden hover:border-cyan-400/30 transition-colors"
              data-testid={`newsletter-post-${p.slug}`}
            >
              {p.cover_image && (
                <img src={p.cover_image} alt={p.title} className="w-full h-56 md:h-72 object-cover" />
              )}
              <div className="p-6 md:p-8">
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3 font-orbitron tracking-widest uppercase">
                  <Calendar className="w-3.5 h-3.5" />
                  {new Date(p.created_at).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
                <h2 className="font-orbitron text-2xl md:text-3xl text-white mb-3">{p.title}</h2>
                {p.excerpt && <p className="text-gray-400 mb-4">{p.excerpt}</p>}
                <Link
                  to={`/newsletter/${p.slug}`}
                  className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-semibold"
                  data-testid={`newsletter-read-${p.slug}`}
                >
                  Read more →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

export function NewsletterPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/newsletter-posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div className="min-h-screen bg-[var(--void)] text-gray-200">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <Link
          to="/newsletter"
          className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 text-sm font-orbitron tracking-widest uppercase"
          data-testid="newsletter-post-back"
        >
          <ArrowLeft className="w-4 h-4" /> All Posts
        </Link>
        {loading && <p className="text-gray-500">Loading…</p>}
        {error && <p className="text-gray-400">Post not found.</p>}
        {post && (
          <article data-testid="newsletter-post-article">
            {post.cover_image && (
              <img src={post.cover_image} alt={post.title} className="w-full rounded-3xl border border-white/10 mb-8" />
            )}
            <p className="text-gray-500 text-xs uppercase tracking-widest font-orbitron mb-3">
              {new Date(post.created_at).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
            <h1 className="font-orbitron text-4xl md:text-5xl font-bold gradient-text mb-8">{post.title}</h1>
            <div className="prose prose-invert max-w-none text-gray-300 leading-relaxed whitespace-pre-line">
              {post.body}
            </div>
          </article>
        )}
      </div>
    </div>
  );
}
